import { activityKindKey, LEGACY_TYPE_DECOMPOSITION } from "@/constants/activity";
import { QuestionMetrics } from "@/constants/conductSelfPractice";
import { ActivityTypeConfig, ActivityTypeConfigs } from "@/constants/createActivity";
import { StudentOmrFilterProps } from "@/constants/global";
import {
  ActivityCategoryEnum,
  ActivityScopeEnum,
  ActivitySubtypeEnum,
  ActivityTypeEnum,
  ActivityTypeIdsEnum,
} from "@/enums/activity";
import { QuestionReportStatusEnum, StudentOmrStatusEnum } from "@/enums/conductTest";
import { QuestionTypeIdsEnum } from "@/enums/content";
import { QuestionStatusEnum } from "@/enums/selfPractice";
import {
  getFormattedDurationWithUnits,
  getFormattedPercentage,
  getNameFromEnumId,
} from "@/methods/utils";
import { useIonModalStore } from "@/stores/core/ionModalStore";
import {
  ActivityClassification,
  ActivityQuestion,
  ActivityQuestionCreate,
  ActivitySection,
  ActivitySectionConfig,
  ActivitySectionInput,
  CreateActivitySection,
  PredesignedActivity,
  PredesignedActivityInfo,
  SaveActivityQuestionsInputs,
} from "@/types/activity";
import { ComponentColorType } from "@/types/componentType";
import {
  ActivityEvent,
  ActivityEventView,
  QuestionEvaluation,
  QuestionPosition,
} from "@/types/conductActivity";
import { SelfPracticeQuestionState } from "@/types/conductSelfPractice";
import { StudentOmrState } from "@/types/offlineActivity";
import { QuestionDetails } from "@/types/studentActivityReport";

// Per-kind authoring config looked up by the (Type, Scope, Category) combination.
export function getActivityTypeConfig(classification: ActivityClassification): ActivityTypeConfig {
  return ActivityTypeConfigs[activityKindKey(classification)];
}

// ──────────────────────────────────────────────
// Save-boundary wire mappers (AR-7.3 / AR-7.4)
// Explicit-key mappers strip UI view-model fields so the request carries exactly the
// activity.json wire keys. A future view-only field then fails the contract test instead
// of silently leaking on the wire.
// ──────────────────────────────────────────────

// UI editor question → activity.json ActivityQuestionInput. Drops the four legacy content
// axes (SubjectId/SubjectChapterId/TopicId/ConceptId) and keeps TopicConceptId.
export function toActivityQuestionCreate(question: ActivityQuestion): ActivityQuestionCreate {
  return {
    QuestionId: question.QuestionId,
    QuestionSerialNo: question.QuestionSerialNo,
    QuestionTypeId: question.QuestionTypeId,
    QuestionSubtypeId: question.QuestionSubtypeId,
    DifficultyLevelId: question.DifficultyLevelId,
    MaximumMarks: question.MaximumMarks,
    NegativeMarks: question.NegativeMarks,
    // ⚠️ AR-13.5 pending: the question-picker cannot populate TopicConceptId until
    // get-concepts-for-topic lands, so it defaults to 0 today (RUNTIME gap, not a type gap).
    TopicConceptId: question.TopicConceptId ?? 0,
  };
}

// Working section → activity.json ActivitySectionInput (Questions stripped to the wire rows).
// ActivitySection has no view-only fields beyond `Questions`, so spreading the rest is safe:
// only `Questions` needs the wire-row mapping.
export function toActivitySectionInput(section: ActivitySection): ActivitySectionInput {
  // Subjects is a response-only field (get-activity-data) — never sent on the create/save wire.
  const { Questions, Subjects, ...sectionFields } = section;
  void Subjects;
  return {
    ...sectionFields,
    Questions: Questions ? Questions.map(toActivityQuestionCreate) : null,
  };
}

// The backend's new.ActivityQuestion table enforces UNIQUE(ActivityId, QuestionSerialNo) with
// serials continuous across sections, while the editor numbers questions per section (each
// section restarts at 1) — a multi-section save would collide on (ActivityId, 1). Renumber
// activity-wide at the wire boundary, preserving section order and each section's serial order.
// Mutates in place: call ONLY on freshly-mapped wire sections (the wire mappers copy every
// question row), never on working view-model sections.
export function renumberQuestionSerialsAcrossSections<
  WireSection extends { Questions: Array<ActivityQuestionCreate> | null },
>(sections: Array<WireSection>): Array<WireSection> {
  let activitySerialNo = 0;
  for (const section of sections) {
    if (!section.Questions) {
      continue;
    }
    section.Questions.sort((a, b) => a.QuestionSerialNo - b.QuestionSerialNo);
    for (const question of section.Questions) {
      activitySerialNo += 1;
      question.QuestionSerialNo = activitySerialNo;
    }
  }
  return sections;
}

// Working sections → save-activity-questions wire body (activity.json SaveActivityQuestionsInputs).
export function toSaveActivityQuestionsInputs(
  activityId: number,
  sections: Array<ActivitySection>,
  finalizeActivity: boolean,
  affiliationId: number | null,
): SaveActivityQuestionsInputs {
  return {
    ActivityId: activityId,
    AffiliationId: affiliationId,
    Sections: renumberQuestionSerialsAcrossSections(sections.map(toActivitySectionInput)),
    FinalizeActivity: finalizeActivity,
  };
}

// Client event view → activity.json ActivityEvent (strips the client-only IsSaved flag).
export function toActivityEventWire(event: ActivityEventView): ActivityEvent {
  const { IsSaved, ...wireEvent } = event;
  void IsSaved; // client-only push-tracking flag — never sent to the backend (AR-7.4)
  return wireEvent;
}

export function mapSectionsToConfigs(
  sections: Array<CreateActivitySection>,
): Array<ActivitySectionConfig> {
  return sections.map((section) => ({
    SectionName: section.SectionName,
    QuestionTypeId: section.QuestionTypes[0].Id,
    TotalQuestions: section.TotalQuestions,
    AllowedAttempts: section.AllowedAttempts,
    MarksPerQuestion: section.MarksPerQuestion,
    NegativeMarks: section.NegativeMarks,
  }));
}

export function getQuestionTypeString(questionTypeIds: Array<number>) {
  return questionTypeIds[0] === 0 || questionTypeIds.length > 1
    ? "Mixed"
    : getNameFromEnumId(questionTypeIds[0], QuestionTypeIdsEnum);
}

export function getQuestionPositionAfterMove(
  activityQuestions: Array<Array<number>>,
  currentPosition: QuestionPosition | undefined,
  newPosition: QuestionPosition | null,
  sectionOffset: number,
  questionOffset: number,
) {
  const updatedPosition =
    currentPosition !== undefined ? { ...currentPosition } : { SectionIndex: 0, QuestionIndex: 0 };
  if (newPosition !== null) {
    updatedPosition.SectionIndex = newPosition.SectionIndex;
    updatedPosition.QuestionIndex = newPosition.QuestionIndex;
  } else {
    if (sectionOffset !== 0) {
      updatedPosition.SectionIndex += sectionOffset;
      if (updatedPosition.SectionIndex === -1) {
        updatedPosition.SectionIndex = activityQuestions.length - 1;
      } else if (updatedPosition.SectionIndex >= activityQuestions.length) {
        updatedPosition.SectionIndex = 0;
      }
      updatedPosition.QuestionIndex =
        sectionOffset > 0 ? 0 : activityQuestions[updatedPosition.SectionIndex].length - 1;
    } else if (questionOffset !== 0) {
      updatedPosition.QuestionIndex += questionOffset;
      if (updatedPosition.QuestionIndex === -1) {
        updatedPosition.SectionIndex -= 1;
        if (updatedPosition.SectionIndex === -1) {
          updatedPosition.SectionIndex = activityQuestions.length - 1;
        }
        updatedPosition.QuestionIndex = activityQuestions[updatedPosition.SectionIndex].length - 1;
      } else if (
        updatedPosition.QuestionIndex >= activityQuestions[updatedPosition.SectionIndex].length
      ) {
        updatedPosition.SectionIndex += 1;
        if (updatedPosition.SectionIndex >= activityQuestions.length) {
          updatedPosition.SectionIndex = 0;
        }
        updatedPosition.QuestionIndex = 0;
      }
    }
  }
  return activityQuestions[updatedPosition.SectionIndex][updatedPosition.QuestionIndex];
}

export function getActivityQuestionEvaluation(
  questionState: QuestionDetails | null | undefined,
): QuestionEvaluation | undefined {
  if (!questionState || !questionState.IsAttempted) {
    return undefined;
  }
  let evaluationMessage;
  if (questionState.IsCorrect) {
    evaluationMessage = {
      MessageTitle: "Correct",
      MessageType: "success" as ComponentColorType,
      MessageText: `Well done! You answered this question correctly in ${questionState.TimeTaken}.`,
      MessageIcon: "nudges/corrected-count",
    };
  } else if (questionState.QuestionStatus == QuestionReportStatusEnum.EVALUATION_PENDING) {
    evaluationMessage = {
      MessageTitle: "Evaluation Pending",
      MessageType: "info" as ComponentColorType,
      MessageText: "Your answer has been submitted and is awaiting evaluation by the teacher.",
      MessageIcon: "nudges/evaluation-pending", // use the appropriate icon
    };
  } else {
    const messageText = `Oops! You got this question wrong after spending ${questionState.TimeTaken}.`;
    evaluationMessage = {
      MessageTitle: "Wrong",
      MessageType: "danger" as ComponentColorType,
      MessageText: messageText,
      MessageIcon: "nudges/wrong-answer",
    };
  }
  const questionEvaluation: QuestionEvaluation = {
    CorrectAnswer: questionState.CorrectAnswer,
    SubmittedAnswer: questionState.SelectedAnswer ? questionState.SelectedAnswer : undefined,
    // ?? null: interactive-video report details ride the same QuestionDetails type but their
    // wire may omit the key — absent must read as "not annotated", never as "toggle hidden".
    EvaluatedAnswer: questionState.EvaluatedAnswer ?? null,
    IsSubmittedCorrect: !!questionState.IsCorrect,
    ...evaluationMessage,
  };
  return questionEvaluation;
}

export function getStudentOmrCardClasses(student: StudentOmrState) {
  const cardColor =
    StudentOmrFilterProps[student.StudentStatus ?? StudentOmrStatusEnum.PENDING].color;
  return cardColor
    ? `color-${cardColor}-dark bg-card-${cardColor}`
    : "color-text bg-card-text-light";
}

export function getPracticeQuestionEvaluation(
  questionState: SelfPracticeQuestionState | null | undefined,
  evaluateSecondAttempt: boolean,
): QuestionEvaluation | undefined {
  if (
    !questionState ||
    !questionState.IsAttempted ||
    (questionState.IsSkipped && questionState.QuestionStatus != QuestionStatusEnum.SECOND_SKIPPED)
  ) {
    return undefined;
  }
  const accuracyMessage = questionState.Accuracy
    ? getFormattedPercentage(questionState.Accuracy, 0)
    : QuestionMetrics.Accuracy;
  const timeMessage = questionState.AvgTime
    ? getFormattedDurationWithUnits(questionState.AvgTime)
    : QuestionMetrics.AvgTime;
  const metricsMessage = `${accuracyMessage} solved this question correctly in ${timeMessage}.`;
  let evaluationMessage;
  if (questionState.IsCorrect || (evaluateSecondAttempt && questionState.IsSecondCorrect)) {
    evaluationMessage = {
      MessageTitle: "Correct",
      MessageType: "success" as ComponentColorType,
      MessageText: `Well done! ${metricsMessage}`,
      MessageIcon: "nudges/corrected-count",
    };
  } else {
    const messageText = evaluateSecondAttempt
      ? `Oops! Wrong answer. ${metricsMessage}`
      : `Oops! Wrong ${
          questionState.IsSecondAttempted
            ? `again. ${metricsMessage}`
            : "answer. Review the concepts and try again."
        }`;
    evaluationMessage = {
      MessageTitle: "Wrong",
      MessageType: "danger" as ComponentColorType,
      MessageText: messageText,
      MessageIcon: "nudges/wrong-answer",
    };
  }
  let submittedAnswer =
    questionState.IsAttempted && questionState.SelectedAnswer
      ? questionState.SelectedAnswer
      : undefined;
  let isSubmittedCorrect = !!questionState.IsCorrect;
  if (
    evaluateSecondAttempt &&
    questionState.IsSecondAttempted &&
    questionState.SecondSelectedAnswer
  ) {
    submittedAnswer = questionState.SecondSelectedAnswer;
    isSubmittedCorrect = !!questionState.IsSecondCorrect;
  }
  const questionEvaluation: QuestionEvaluation = {
    CorrectAnswer: questionState.CorrectAnswer,
    SubmittedAnswer: submittedAnswer,
    IsSubmittedCorrect: isSubmittedCorrect,
    ...evaluationMessage,
  };
  return questionEvaluation;
}

export async function onPublishPredesignedActivity(
  activity: PredesignedActivity,
  classification: ActivityClassification,
  forceReload: () => Promise<void>,
  isStackedModal: boolean = false,
  teachingActivityId: number | null = null,
) {
  const ionModalStore = useIonModalStore();
  const { openStackedSheetModal, openSheetModal } = ionModalStore;
  const preloadedActivityInfo: PredesignedActivityInfo = {
    ActivityId: activity.ActivityId,
    Classification: classification,
    ActivityName: activity.ActivityName,
    CourseId: activity.CourseId,
    PublishedBatchIds: activity.PublishedBatchIds ?? [],
    TeachingActivityId: teachingActivityId,
  };
  const componentProps = {
    preloadedActivityInfo: preloadedActivityInfo,
    isStackedModal: isStackedModal,
    onForceReload: async () => await forceReload(),
  };
  const modalProps = { ModalTitle: "Publish Activity", ModalColor: "success" };
  const { default: ModalPublishPredesignedActivity } =
    await import("@/components/modals/activity/ModalPublishPredesignedActivity.vue");
  if (isStackedModal) {
    await openStackedSheetModal(ModalPublishPredesignedActivity, componentProps, modalProps);
  } else {
    await openSheetModal(ModalPublishPredesignedActivity, componentProps, modalProps);
  }
}

export async function onPreviewPredesignedActivity(activityId: number) {
  const ionModalStore = useIonModalStore();
  const { openInlineModal } = ionModalStore;
  const { default: ModalPreviewActivity } =
    await import("@/components/modals/activity/ModalPreviewActivity.vue");
  const componentProps = {
    activityId: activityId,
    allowPdfDownload: false,
  };
  const modalProps = { CustomCssClass: "preview-preloaded-activity-modal" };
  await openInlineModal(ModalPreviewActivity, componentProps, modalProps);
}

// ──────────────────────────────────────────────
// Four-axis classification helpers (backend /v7)
// ──────────────────────────────────────────────

// Decompose a legacy compound ActivityTypeId (a page-intent token, never a
// server-returned value) into the new (Type, Scope, Category) triple.
export function decomposeActivityTypeId(legacyActivityTypeId: number): ActivityClassification {
  return (
    LEGACY_TYPE_DECOMPOSITION[legacyActivityTypeId as ActivityTypeIdsEnum] ?? {
      ActivityTypeId: legacyActivityTypeId,
      ActivityScopeId: ActivityScopeEnum.STUDENT,
      ActivityCategoryId: ActivityCategoryEnum.DEFAULT,
    }
  );
}

// Map a legacy page-intent token to the new `activityTypeIds` filter array (a
// single 1–5 TYPE value). Replaces getMappedActivityTypeIds for the migrated
// list endpoints, which now key on the new type + (where present) scope.
export function getActivityTypeIdsForFilter(legacyActivityTypeId: number): Array<number> {
  return [decomposeActivityTypeId(legacyActivityTypeId).ActivityTypeId];
}

// Single-axis predicates — use these to branch on server-returned classification.
export const isStudentScope = (scopeId: number): boolean => scopeId === ActivityScopeEnum.STUDENT;
export const isInstituteScope = (scopeId: number): boolean =>
  scopeId === ActivityScopeEnum.INSTITUTE;
export const isInternalScope = (scopeId: number): boolean => scopeId === ActivityScopeEnum.INTERNAL;

export const isPracticeType = (typeId: number): boolean => typeId === ActivityTypeEnum.PRACTICE;
export const isAssignmentType = (typeId: number): boolean => typeId === ActivityTypeEnum.ASSIGNMENT;
export const isTestType = (typeId: number): boolean => typeId === ActivityTypeEnum.TEST;
export const isInteractiveVideoType = (typeId: number): boolean =>
  typeId === ActivityTypeEnum.INTERACTIVE_VIDEO;
export const isWorkbookType = (typeId: number): boolean => typeId === ActivityTypeEnum.WORKBOOK;

export const isDefaultCategory = (categoryId: number): boolean =>
  categoryId === ActivityCategoryEnum.DEFAULT;
export const isPredesignedCategory = (categoryId: number): boolean =>
  categoryId === ActivityCategoryEnum.PREDESIGNED;
export const isPastYearCategory = (categoryId: number): boolean =>
  categoryId === ActivityCategoryEnum.PAST_YEAR;
export const isSeriesCategory = (categoryId: number): boolean =>
  categoryId === ActivityCategoryEnum.SERIES;
export const isOlympiadCategory = (categoryId: number): boolean =>
  categoryId === ActivityCategoryEnum.OLYMPIAD;

// Activities exempt from forced fullscreen during conduct: all assignments and
// interactive videos (replaces the legacy FullscreenExemptActivityTypeIds list).
export const isFullscreenExempt = (typeId: number): boolean =>
  isAssignmentType(typeId) || isInteractiveVideoType(typeId);

// Display labels — the backend transmits the integer only, so labelling is the UI's job.
export const ActivityTypeLabels: Record<ActivityTypeEnum, string> = {
  [ActivityTypeEnum.PRACTICE]: "Practice",
  [ActivityTypeEnum.ASSIGNMENT]: "Assignment",
  [ActivityTypeEnum.TEST]: "Test",
  [ActivityTypeEnum.INTERACTIVE_VIDEO]: "Interactive Video",
  [ActivityTypeEnum.WORKBOOK]: "Workbook",
};

export const ActivityScopeLabels: Record<ActivityScopeEnum, string> = {
  [ActivityScopeEnum.STUDENT]: "Student",
  [ActivityScopeEnum.INSTITUTE]: "Institute",
  [ActivityScopeEnum.INTERNAL]: "Internal",
};

export const ActivityCategoryLabels: Record<ActivityCategoryEnum, string> = {
  [ActivityCategoryEnum.DEFAULT]: "Default",
  [ActivityCategoryEnum.PREDESIGNED]: "Predesigned",
  [ActivityCategoryEnum.PAST_YEAR]: "Past Year Paper",
  [ActivityCategoryEnum.SERIES]: "Test Series",
  [ActivityCategoryEnum.OLYMPIAD]: "Olympiad",
};

export const ActivitySubtypeLabels: Record<ActivitySubtypeEnum, string> = {
  [ActivitySubtypeEnum.CHAPTER_BEGINNER]: "Chapter — Beginner",
  [ActivitySubtypeEnum.CHAPTER_CHALLENGER]: "Chapter — Challenger",
  [ActivitySubtypeEnum.CHAPTER_ADVANCED]: "Chapter — Advanced",
  [ActivitySubtypeEnum.CONCEPT_CHECK]: "Concept Check",
};

// Compose a user-facing label for an activity from its classification axes.
export function getActivityKindLabel(classification: {
  ActivityTypeId: number;
  ActivityCategoryId?: number;
}): string {
  const typeLabel =
    ActivityTypeLabels[classification.ActivityTypeId as ActivityTypeEnum] ?? "Activity";
  switch (classification.ActivityCategoryId) {
    case ActivityCategoryEnum.SERIES:
      return "Test Series";
    case ActivityCategoryEnum.PAST_YEAR:
      return "Past Year Paper";
    case ActivityCategoryEnum.OLYMPIAD:
      return `Olympiad ${typeLabel}`;
    case ActivityCategoryEnum.PREDESIGNED:
      return `Predesigned ${typeLabel}`;
    default:
      return typeLabel;
  }
}
